// src/screens/Flashcards.jsx
import React, { useState, useEffect } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import { selectWeeklyWords } from "../data/wordSelector";
import { loadProgress, saveProgress } from "../data/progressStorage";
import { loadVocabPair } from "../data/loadVocabPair";
import { parseLanguageId, languageLabel } from "../data/languages";
import { isSpeechSupported, speak } from "../utils/speech";
import {
  applyReviewResult,
  collectDueWords,
  isInReview,
  maxReviewsPerSession,
} from "../data/spacedRepetition";
import { Button, Panel, ProgressBar, ScreenHeader, Segmented, Stack } from "../components/ui";

const WEEKLY_KEY_PREFIX = "fluent_weeklyset_";

function getWeeklyKey(languageId, pace, totalWords, profileId, weekIndex = 1) {
  const lang = languageId || "generic";
  const p = pace || "medium";
  const t = totalWords || 0;
  const profile = profileId || "default";
  const w = weekIndex || 1;
  return `${WEEKLY_KEY_PREFIX}${profile}_${lang}_${p}_${t}_w${w}`;
}

function loadWeeklySetLocal(
  languageId,
  pace,
  totalWords,
  profileId,
  weekIndex = 1
) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(
      getWeeklyKey(languageId, pace, totalWords, profileId, weekIndex)
    );
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Array.isArray(data) && data.length > 0) return data;
    return null;
  } catch (e) {
    console.warn("Fluent! weekly set load error (local):", e);
    return null;
  }
}

function saveWeeklySetLocal(
  languageId,
  pace,
  totalWords,
  weeklySet,
  profileId,
  weekIndex = 1
) {
  if (typeof window === "undefined") return;
  try {
    const key = getWeeklyKey(
      languageId,
      pace,
      totalWords,
      profileId,
      weekIndex
    );
    window.localStorage.setItem(key, JSON.stringify(weeklySet));
  } catch (e) {
    console.warn("Fluent! weekly set save error (local):", e);
  }
}

// zufällig n Elemente
function pickRandom(arr, n) {
  if (n <= 0) return [];
  if (n >= arr.length) return [...arr];
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

function shuffleArray(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Manche weekly_sets-Zeilen in Supabase sind noch unter dem alten Schema gecacht
// ({es, de} statt {source, target}) - solange die nicht neu erzeugt wurden, hier
// beide Formen akzeptieren statt leere Karten anzuzeigen.
function wordSource(w) {
  return w.source ?? w.es;
}
function wordTarget(w) {
  return w.target ?? w.de;
}

function buildQuizCardsFromWords(words, sourceLang, targetLang) {
  const sourceLabel = languageLabel(sourceLang);
  const targetLabel = languageLabel(targetLang);

  return words.map((w) => {
    const forward = Math.random() < 0.5;
    const source = wordSource(w);
    const target = wordTarget(w);

    if (forward) {
      return {
        ...w,
        direction: "source-target",
        promptLang: sourceLabel,
        answerLang: targetLabel,
        prompt: source,
        answer: target,
      };
    } else {
      return {
        ...w,
        direction: "target-source",
        promptLang: targetLabel,
        answerLang: sourceLabel,
        prompt: target,
        answer: source,
      };
    }
  });
}

// Lautsprecher-Button: liest den Text in der angegebenen Sprache vor.
function SpeakButton({ text, lang }) {
  if (!isSpeechSupported()) return null;
  return (
    <button
      type="button"
      className="speak-button"
      aria-label="Aussprache anhören"
      title="Aussprache anhören"
      onClick={() => speak(text, lang)}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
          d="M4 9v6h4l5 4V5L8 9H4z"
          fill="currentColor"
        />
        <path
          d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

// Leitner-Update
function updateBox(prevBox, isRight, isNew) {
  if (isNew) {
    if (isRight) return 3;
    return 1;
  }
  const current = prevBox || 1;
  if (isRight) return Math.min(current + 1, 5);
  return Math.max(current - 1, 1);
}

// Session-Auswahl: höchstens 25 % fällige Wiederholungen (wochenübergreifend, am längsten
// überfällige zuerst), der Rest aus dem Wochenset nach Stufen gewichtet. Wörter in der
// Wiederholung, die noch nicht fällig sind, füllen nur auf, wenn die Woche sonst leer ist.
function selectSessionWords(weeklySet, progress, sessionSize, wordsById) {
  const reviews = collectDueWords(progress, wordsById).slice(
    0,
    maxReviewsPerSession(sessionSize)
  );
  const reviewIds = new Set(reviews.map((w) => w.id));
  const weeklySize = sessionSize - reviews.length;

  const byBox = { 1: [], 2: [], 3: [], 4: [] };

  for (const w of weeklySet) {
    const state = progress[w.id];
    if (reviewIds.has(w.id) || isInReview(state)) continue;
    const box = state?.box >= 1 && state?.box <= 4 ? state.box : 1;
    byBox[box].push(w);
  }

  const desired = {
    1: Math.round(weeklySize * 0.4),
    2: Math.round(weeklySize * 0.3),
    3: Math.round(weeklySize * 0.2),
    4: Math.round(weeklySize * 0.1),
  };

  let selected = [];

  for (let box = 1; box <= 4; box++) {
    const pool = byBox[box];
    const need = desired[box] ?? 0;
    if (!pool || pool.length === 0 || need <= 0) continue;
    selected.push(...pickRandom(pool, need));
  }

  if (selected.length < weeklySize) {
    const already = new Set(selected.map((w) => w.id));
    const notInReview = weeklySet.filter(
      (w) => !already.has(w.id) && !reviewIds.has(w.id) && !isInReview(progress[w.id])
    );
    selected.push(...pickRandom(notInReview, weeklySize - selected.length));
  }

  if (selected.length < weeklySize) {
    const already = new Set(selected.map((w) => w.id));
    const rest = weeklySet.filter(
      (w) => !already.has(w.id) && !reviewIds.has(w.id)
    );
    selected.push(...pickRandom(rest, weeklySize - selected.length));
  }

  return shuffleArray([
    ...selected,
    ...reviews.map((w) => ({ ...w, isReview: true })),
  ]);
}

// Box-Verteilung
function computeBoxCounts(weeklySet, progress) {
  const counts = { unseen: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const w of weeklySet) {
    const state = progress[w.id];
    if (!state) {
      counts.unseen += 1;
    } else {
      const box = state.box >= 1 && state.box <= 5 ? state.box : 1;
      counts[box] += 1;
    }
  }
  return counts;
}

export default function Flashcards({
  onBack,
  language,
  pace,
  weeklyWords,
  profileId,
  weekIndex = 1,
}) {
  const effectiveLanguage = language || "es-de";
  const effectivePace = pace || "medium";
  const effectiveWeeklyWords = weeklyWords || 50;
  const effectiveProfile = profileId || "default";
  const effectiveWeekIndex = weekIndex || 1;
  const { source: sourceLang, target: targetLang } =
    parseLanguageId(effectiveLanguage) || { source: "es", target: "de" };

    console.log("[Flashcards] effectiveWeekIndex =", effectiveWeekIndex);


  const [weeklySet, setWeeklySet] = useState([]);
  // Alle Vokabeln des Sprachpaars (id → Wort), für Wiederholungen aus anderen Wochen
  const [wordsById, setWordsById] = useState(() => new Map());
  const [progress, setProgress] = useState({});
  const [sessionSize, setSessionSize] = useState(10);

  const [mode, setMode] = useState("menu"); // "menu" | "quiz" | "finished"
  const [quizCards, setQuizCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [isFlipped, setIsFlipped] = useState(false);
  const [instantReset, setInstantReset] = useState(false);
  const [rightCount, setRightCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);

  // Weekly-Set + Progress laden (Supabase ↔ local, aber: eingeloggt → nur Supabase für WeeklySet)
  useEffect(() => {
    let cancelled = false;

    async function init() {
      let userId = null;
      let weekly = null;
      const vocabPairPromise = loadVocabPair(sourceLang, targetLang);

      // 0) User holen
      try {
        const { data: userData } = await supabase.auth.getUser();
        userId = userData?.user?.id || null;
      } catch (e) {
        console.warn("Flashcards: getUser failed:", e);
      }

      if (userId) {
        // Eingeloggt → WeeklySet nur aus Supabase oder neu erzeugen + hochladen
        try {
          const { data, error } = await supabase
            .from("weekly_sets")
            .select("words")
            .eq("user_id", userId)
            .eq("profile_id", effectiveProfile)
            .eq("language_id", effectiveLanguage)
            .eq("pace", effectivePace)
            .eq("total_words", effectiveWeeklyWords)
            .eq("week_index", effectiveWeekIndex)
            .maybeSingle();

          if (
            !error &&
            data &&
            Array.isArray(data.words) &&
            data.words.length > 0
          ) {
            weekly = data.words;
          }
        } catch (e) {
          console.warn("Flashcards: remote weekly_sets load failed:", e);
        }

        // Wenn es noch kein WeeklySet in Supabase gibt → neu erzeugen
        if (!weekly) {
          const vocabOverride = await vocabPairPromise;

          if (cancelled) return;

          // IDs aus früheren Wochen einsammeln, um Dopplungen zu vermeiden
          const excludedIds = new Set();
          try {
            const { data: existingRows, error: existingError } = await supabase
              .from("weekly_sets")
              .select("week_index, words")
              .eq("user_id", userId)
              .eq("profile_id", effectiveProfile)
              .eq("language_id", effectiveLanguage)
              .eq("pace", effectivePace);

            if (!existingError && Array.isArray(existingRows)) {
              for (const row of existingRows) {
                if (
                  row.week_index < effectiveWeekIndex &&
                  Array.isArray(row.words)
                ) {
                  row.words.forEach((w) => {
                    if (w?.id) excludedIds.add(w.id);
                  });
                }
              }
            }
          } catch (e) {
            console.warn(
              "Flashcards: load existing weekly_sets for exclusions failed:",
              e
            );
          }

          weekly = selectWeeklyWords({
            languageId: effectiveLanguage,
            pace: effectivePace,
            totalWords: effectiveWeeklyWords,
            vocabOverride,
            excludedIds,
          });

          // In Supabase spiegeln
          try {
            const { error: upsertError } = await supabase
              .from("weekly_sets")
              .upsert(
                {
                  user_id: userId,
                  profile_id: effectiveProfile,
                  language_id: effectiveLanguage,
                  pace: effectivePace,
                  total_words: effectiveWeeklyWords,
                  week_index: effectiveWeekIndex,
                  words: weekly,
                },
                {
                  onConflict: "user_id,profile_id,language_id,pace,week_index",
                }
              );
            if (upsertError) {
              console.warn(
                "Flashcards: remote weekly_sets upsert failed:",
                upsertError
              );
            }
          } catch (e) {
            console.warn("Flashcards: remote weekly_sets upsert threw:", e);
          }
        }
      } else {
        // Nicht eingeloggt → lokaler Fallback wie bisher, aber mit Wochenindex + Exclusions
        weekly =
          loadWeeklySetLocal(
            effectiveLanguage,
            effectivePace,
            effectiveWeeklyWords,
            effectiveProfile,
            effectiveWeekIndex
          ) || null;

        if (!weekly) {
          const vocabOverride = await vocabPairPromise;

          if (cancelled) return;

          // Excluded IDs aus früheren Wochen (lokal) sammeln
          const excludedIds = new Set();
          for (let wIndex = 1; wIndex < effectiveWeekIndex; wIndex++) {
            const prev = loadWeeklySetLocal(
              effectiveLanguage,
              effectivePace,
              effectiveWeeklyWords,
              effectiveProfile,
              wIndex
            );
            if (Array.isArray(prev)) {
              prev.forEach((w) => w?.id && excludedIds.add(w.id));
            }
          }

          weekly = selectWeeklyWords({
            languageId: effectiveLanguage,
            pace: effectivePace,
            totalWords: effectiveWeeklyWords,
            vocabOverride,
            excludedIds,
          });

          // local speichern
          saveWeeklySetLocal(
            effectiveLanguage,
            effectivePace,
            effectiveWeeklyWords,
            weekly,
            effectiveProfile,
            effectiveWeekIndex
          );
        }
      }

      const allWords = await vocabPairPromise;

      if (cancelled) return;

      setWeeklySet(weekly);
      setWordsById(new Map(allWords.map((w) => [w.id, w])));

      const loadedProgress = loadProgress(
        effectiveLanguage,
        effectiveProfile
      );
      setProgress(loadedProgress || {});

      setMode("menu");
      setRightCount(0);
      setWrongCount(0);
      setIsFlipped(false);
      setInstantReset(true);
      setCurrentIndex(0);
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [
    effectiveLanguage,
    effectivePace,
    effectiveWeeklyWords,
    effectiveProfile,
    effectiveWeekIndex,
    sourceLang,
    targetLang,
  ]);

  useEffect(() => {
    if (instantReset) {
      const id = setTimeout(() => setInstantReset(false), 0);
      return () => clearTimeout(id);
    }
  }, [instantReset]);

  const totalWeekly = weeklySet.length;

  function startQuiz() {
    if (!totalWeekly) return;

    const n = Math.min(sessionSize, totalWeekly);
    const sessionWords = selectSessionWords(weeklySet, progress, n, wordsById);
    const cards = buildQuizCardsFromWords(sessionWords, sourceLang, targetLang);

    setQuizCards(cards);
    setCurrentIndex(0);
    setRightCount(0);
    setWrongCount(0);
    setIsFlipped(false);
    setInstantReset(false);
    setMode("quiz");
  }

  function handleShowAnswer() {
    setInstantReset(false);
    setIsFlipped(true);
  }

  function handleAnswer(isRight) {
    const currentCard = quizCards[currentIndex];
    if (!currentCard) return;

    if (isRight) setRightCount((c) => c + 1);
    else setWrongCount((c) => c + 1);

    setProgress((prev) => {
      const existedBefore = !!prev[currentCard.id];
      const prevState = prev[currentCard.id] || {
        box: 1,
        correct: 0,
        wrong: 0,
      };
      const newBox = updateBox(prevState.box, isRight, !existedBefore);
      const now = Date.now();
      const newState = applyReviewResult({
        prevState: existedBefore ? prevState : null,
        nextState: {
          ...prevState,
          box: newBox,
          lastReviewed: now,
          correct: (prevState.correct || 0) + (isRight ? 1 : 0),
          wrong: (prevState.wrong || 0) + (!isRight ? 1 : 0),
        },
        isRight,
        wasDue: !!currentCard.isReview,
        inWeek: weeklySet.some((w) => w.id === currentCard.id),
        now,
      });

      const updated = {
        ...prev,
        [currentCard.id]: newState,
      };

      saveProgress(effectiveLanguage, effectiveProfile, updated);
      return updated;
    });

    const nextIndex = currentIndex + 1;

    if (nextIndex >= quizCards.length) {
      setInstantReset(true);
      setIsFlipped(false);
      setMode("finished");
      return;
    }

    setInstantReset(true);
    setIsFlipped(false);
    setCurrentIndex(nextIndex);
  }

  function backToMenu() {
    setMode("menu");
    setIsFlipped(false);
    setInstantReset(true);
    setCurrentIndex(0);
  }

  /* ---------- MENU VIEW ---------- */
  if (mode === "menu") {
    const total = totalWeekly || 0;
    const boxCounts = computeBoxCounts(weeklySet, progress);
    const unseenCount = boxCounts.unseen || 0;
    const reviewedCount = total - unseenCount;
    const reviewedPercent = total > 0 ? (reviewedCount / total) * 100 : 0;
    const dueCount = collectDueWords(progress, wordsById).length;

    const levelLabels = {
      1: "Stufe 1 · kritisch",
      2: "Stufe 2 · unsicher",
      3: "Stufe 3 · mittel",
      4: "Stufe 4 · relativ sicher",
      5: "Stufe 5 · sehr sicher",
    };

    const sessionOptions = [5, 10, 15, 20].map((n) => ({ value: n, label: n }));

    return (
      <>
        <ScreenHeader title={`Karteikarten – Woche ${effectiveWeekIndex}`} />

        <Panel variant="muted">
          <div className="flashcard-coverage">
            <svg
              width="64"
              height="64"
              viewBox="0 0 36 36"
              style={{ flexShrink: 0 }}
            >
              <circle
                cx="18"
                cy="18"
                r="15.9155"
                fill="none"
                style={{ stroke: "var(--fluent-track)" }}
                strokeWidth="3"
              />
              {/* Bei 0 % gar nicht zeichnen, sonst bleibt vom runden Linienende ein Punkt sichtbar */}
              {reviewedPercent > 0 && <circle
                cx="18"
                cy="18"
                r="15.9155"
                fill="none"
                style={{ stroke: "var(--fluent-sun)" }}
                strokeWidth="3"
                strokeDasharray={`${reviewedPercent} ${
                  100 - reviewedPercent
                }`}
                strokeDashoffset="25"
                strokeLinecap="round"
                transform="rotate(-90 18 18)"
              />}
              <text
                x="18"
                y="19.5"
                textAnchor="middle"
                fontSize="7"
                fontWeight="600"
                style={{ fill: "var(--fluent-text)" }}
              >
                {total > 0 ? `${reviewedCount}/${total}` : "0/0"}
              </text>
            </svg>

            <div>
              <div className="flashcard-coverage__title">Abdeckung</div>
              <div>
                {reviewedCount} / {total} Wörter abgefragt
              </div>
              <div className="flashcard-coverage__muted">
                Offen: {unseenCount} Wörter
              </div>
              <div className="flashcard-coverage__muted">
                Wiederholungen fällig: {dueCount}
              </div>
            </div>
          </div>

          <Stack gap={3}>
            {[1, 2, 3, 4, 5].map((level) => {
              const count = boxCounts[level] || 0;
              return (
                <ProgressBar
                  key={level}
                  value={total > 0 ? count / total : 0}
                  tone={`level${level}`}
                  label={levelLabels[level]}
                  valueLabel={count}
                />
              );
            })}
          </Stack>
        </Panel>

        <Segmented
          label="Wörter pro Sitzung"
          options={sessionOptions}
          value={sessionSize}
          onChange={setSessionSize}
          className="flashcard-section"
        />

        <Stack gap={2} className="flashcard-actions">
          <Button block onClick={startQuiz} disabled={totalWeekly === 0}>
            Überprüfung starten
          </Button>
          <Button block variant="secondary" onClick={onBack}>
            Zurück zum Hauptmenü
          </Button>
        </Stack>
      </>
    );
  }

  /* ---------- FINISHED VIEW ---------- */
  if (mode === "finished") {
    const totalSession = quizCards.length || 0;

    return (
      <>
        <ScreenHeader
          title="Karteikarten – Ergebnis"
          subtitle={`${rightCount} richtig · ${wrongCount} falsch`}
        />

        <Panel variant="muted">
          <Stack gap={3}>
            <ProgressBar
              value={totalSession > 0 ? rightCount / totalSession : 0}
              tone="right"
              label="Richtig"
              valueLabel={rightCount}
            />
            <ProgressBar
              value={totalSession > 0 ? wrongCount / totalSession : 0}
              tone="wrong"
              label="Falsch"
              valueLabel={wrongCount}
            />
          </Stack>
        </Panel>

        <Stack gap={2} className="flashcard-actions">
          <Button block onClick={backToMenu}>
            Zurück zur Kartenübersicht
          </Button>
          <Button block variant="secondary" onClick={onBack}>
            Zurück zum Hauptmenü
          </Button>
        </Stack>
      </>
    );
  }

  /* ---------- QUIZ VIEW ---------- */
  const currentCard = quizCards[currentIndex];
  const cardNumber = currentIndex + 1;
  const totalSession = quizCards.length || 0;
  // Die Lernsprache (source) steht je nach Richtung vorne oder hinten - dort sitzt der Lautsprecher.
  const sourceOnFront = currentCard?.direction === "source-target";
  const sourceText = currentCard ? wordSource(currentCard) : "";

  return (
    <>
      <ScreenHeader
        title="Karteikarten – Training"
        subtitle={`${languageLabel(targetLang)} ↔ ${languageLabel(sourceLang)}`}
      />

      <ProgressBar
        value={totalSession > 0 ? currentIndex / totalSession : 0}
        label={`Karte ${cardNumber} von ${totalSession}`}
      />

      <div className="flashcard-container">
        <div
          className={
            "flashcard" +
            (isFlipped ? " flipped" : "") +
            (instantReset ? " instant" : "")
          }
        >
          <div className="flashcard-face flashcard-front">
            <div className="flashcard-label">
              Frage ({currentCard?.promptLang})
            </div>
            <div className="flashcard-word">{currentCard?.prompt}</div>
            {sourceOnFront && <SpeakButton text={sourceText} lang={sourceLang} />}
          </div>

          <div className="flashcard-face flashcard-back">
            <div className="flashcard-label">
              Lösung ({currentCard?.answerLang})
            </div>
            <div className="flashcard-word">{currentCard?.answer}</div>
            {!sourceOnFront && <SpeakButton text={sourceText} lang={sourceLang} />}
          </div>
        </div>
      </div>

      <Stack gap={2} className="flashcard-actions">
        {!isFlipped ? (
          <Button block onClick={handleShowAnswer}>
            Umdrehen
          </Button>
        ) : (
          <Stack gap={3} row>
            <Button variant="wrong" onClick={() => handleAnswer(false)}>
              Falsch
            </Button>
            <Button variant="right" onClick={() => handleAnswer(true)}>
              Richtig
            </Button>
          </Stack>
        )}
        <Button block variant="secondary" onClick={backToMenu}>
          Abbrechen
        </Button>
      </Stack>
    </>
  );
}
